import styled from "@emotion/styled";
import { Trans } from "@lingui/react/macro";
import { Dispatch, useMemo } from "react";
import { Answer } from "../../types/test";

import { usePatchTestMutation } from "@/queries/tests/mutations";
import { PublicQuestion, TestType } from "@/queries/types";
import {
  faChevronDown,
  faChevronUp,
  faClock,
} from "@fortawesome/pro-regular-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import * as amplitude from "../../amplitude";
import { Icon } from "../icon";
import { useVerticalCarousel } from "./hooks";
import { Action, State } from "./reducer";
import {
  getPatchTestMutationOptions,
  preparePatchTestMutationData,
} from "./utils/patch-test-mutation";

interface QuestionMenuProps {
  questions: PublicQuestion[];
  state: State;
  dispatch: Dispatch<Action>;
  testType: TestType;
}

//question could be unanswered but only rated
const questionIsAnswered = (answer: Answer | undefined) =>
  !!answer &&
  (!!answer.freeTextAnswer || !!answer.chosenOptions.length || !!answer.video);

export function QuestionNavigation(props: QuestionMenuProps) {
  const {
    containerRef,
    wrapperRef,
    contentRef,
    goNext,
    goPrev,
    canGoNext,
    canGoPrev,
    offset,
    isCarouselActive,
  } = useVerticalCarousel({ slideStep: 48 });
  const { query, asPath } = useRouter();
  const jobOpeningSlug = query.slug as string;
  const testSlug = query.hashedTestId as string;
  const queryClient = useQueryClient();

  const firstQuestionWithNeedMoreTime = useMemo(
    () =>
      props.testType !== "homework"
        ? props.questions.find(
            (question) =>
              question.question_type === "code-input" ||
              question.question_type === "free-text",
          )
        : null,
    [props.testType, props.questions],
  );
  const isPracticeTest = asPath.endsWith("/practice");

  const patchTestMutation = usePatchTestMutation(
    getPatchTestMutationOptions(queryClient),
  );

  const activeQuestionId =
    props.questions[props.state.activeQuestionIndex].test_question_id;
  const activeQuestionAnswer = props.state.answers[activeQuestionId];
  const mutationData = preparePatchTestMutationData(
    jobOpeningSlug,
    testSlug,
    activeQuestionId,
    activeQuestionAnswer,
  );
  return (
    <Container ref={containerRef}>
      {isCarouselActive && (
        <CarouselNavigation onClick={goPrev} disabled={!canGoPrev}>
          <Icon icon={faChevronUp} fontSize={14} color="purple-60" />
        </CarouselNavigation>
      )}

      <CarouselWrapper ref={wrapperRef}>
        <Content style={offset} ref={contentRef}>
          {props.questions.map((question, index) => {
            const needsMoreTime =
              props.testType !== "homework" &&
              (question.question_type === "code-input" ||
                question.question_type === "free-text");

            return (
              <QuestionWrapper key={index}>
                <Button
                  key={question.test_question_id}
                  aria-label={`Show question ${index + 1}`}
                  onClick={() => {
                    if (!isPracticeTest && mutationData) {
                      patchTestMutation.mutate(mutationData);
                    }
                    props.dispatch({
                      type: "select_question",
                      payload: {
                        index,
                      },
                    });

                    amplitude.trackQuestionSelect(
                      props.state.jobOpeningSlug,
                      index,
                    );
                  }}
                >
                  <QuestionNumber
                    active={props.state.activeQuestionIndex === index}
                    answered={questionIsAnswered(
                      props.state.answers[question.test_question_id],
                    )}
                  >
                    {index + 1}
                  </QuestionNumber>
                  {needsMoreTime && (
                    <Tooltip role="tooltip" aria-live="polite">
                      <Trans>Plan more time</Trans>
                    </Tooltip>
                  )}
                </Button>
                {needsMoreTime && (
                  <QuestionNumberPlanMoreTime>
                    <Icon icon={faClock} color="purple-100" fontSize={10} />{" "}
                    {firstQuestionWithNeedMoreTime?.test_question_id ===
                      question.test_question_id && (
                      <PlanMoreTimeText>
                        <Trans>Plan more time</Trans>
                      </PlanMoreTimeText>
                    )}
                  </QuestionNumberPlanMoreTime>
                )}
              </QuestionWrapper>
            );
          })}
        </Content>
      </CarouselWrapper>

      {isCarouselActive && (
        <CarouselNavigation onClick={goNext} disabled={!canGoNext}>
          <Icon icon={faChevronDown} fontSize={14} color="purple-60" />
        </CarouselNavigation>
      )}
    </Container>
  );
}

//region Styles
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  position: relative;
  overflow: initial auto;
  padding: 0 12px 12px;
  height: 100%;

  @media (min-width: 768px) {
    padding-left: 0;
  }
`;

const Tooltip = styled.div`
  display: none;
  position: absolute;
  top: -54px;
  left: -46px;
  z-index: 12;
  width: 128px;
  background-color: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.purple[100]};
  font-size: 12px;
  border-radius: 8px;
  padding: 8px 16px;
  text-align: center;
  margin-bottom: 15px;
  box-shadow: 0px 2px 8px 0px rgba(44, 19, 56, 0.2);

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: calc(50% - 4px);
    width: 8px;
    height: 12px;
    border-style: solid;
    border-width: 6px 4px;
    border-color: ${(props) => props.theme.colors.white} transparent transparent
      transparent;
  }

  @media (min-width: 768px) {
    top: -30px;
    left: 37px;

    &::after {
      left: 7px;
    }
  }
`;

const CarouselWrapper = styled.div`
  overflow-x: hidden;
  width: 100%;

  @media (min-width: 768px) {
    overflow: hidden;
    width: auto;
  }
`;

const CarouselNavigation = styled.button`
  cursor: pointer;
  background: transparent;
  outline: 0;
  padding: 0;
  border: 0;
  width: 36px;
  min-height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background 0.2s;

  &:disabled {
    opacity: 0.4;
  }

  &:hover:not([disabled]) {
    background: ${(props) => props.theme.colors.purple[5]};
    border-radius: 8px;
  }

  @media (min-width: 768px) {
    display: flex;
  }
`;

const Content = styled.div`
  position: relative;
  overflow-x: scroll;
  display: flex;
  flex-direction: row;
  padding-top: 40px;
  column-gap: 12px;

  @media (min-width: 768px) {
    flex-direction: column;
    overflow: hidden;
    padding-top: 0;
    row-gap: 12px;
  }
`;

const QuestionWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;

  @media (min-width: 768px) {
    flex-direction: row;
  }

  &:first-child {
    ${Tooltip} {
      left: 6px;

      &::after {
        left: 8px;
      }
    }
  }

  &:last-child {
    ${Tooltip} {
      left: auto;
      right: 6px;

      &::after {
        left: auto;
        right: 8px;
      }
    }
  }
`;

const QuestionNumber = styled.span<{ active: boolean; answered: boolean }>`
  min-width: 36px;
  padding: 0 8px;
  height: 36px;
  display: flex;
  justify-content: center;
  border: 1px solid ${(props) => props.theme.colors.purple[20]};
  border-radius: 8px;
  align-items: center;
  position: relative;
  font-size: ${12 / 14}rem;
  font-weight: 600;
  background: ${(props) => {
    if (props.active) {
      return props.theme.colors.purple[100];
    } else if (props.answered) {
      return props.theme.colors.orange[100];
    } else {
      return props.theme.colors.white;
    }
  }};
  color: ${(props) =>
    props.answered || props.active
      ? props.theme.colors.white
      : props.theme.colors.purple[100]};
  border-color: ${(props) =>
    props.answered || props.active
      ? "transparent"
      : props.theme.colors.purple[20]};

  & > svg {
    margin-right: 8px;
  }

  @media (min-width: 768px) {
    border-color: transparent;
  }
`;

const QuestionNumberPlanMoreTime = styled.div`
  height: 32px;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 4px;
  margin-bottom: -10px;
  background: ${(props) => props.theme.colors.yellow[50]};
  border-top-right-radius: 8px;
  border-top-left-radius: 8px;

  @media (min-width: 768px) {
    display: flex;
    width: auto;
    height: 36px;
    padding: 13px 10px 13px 16px;
    border-bottom-right-radius: 8px;
    border-top-left-radius: 0;
    margin-left: -8px;
  }

  @media (min-width: 1024px) {
    display: flex;
  }
`;

const PlanMoreTimeText = styled.span`
  display: none;
  font-size: 10px;
  margin-left: 4px;

  @media (min-width: 1024px) {
    display: inline-block;
  }
`;

const Button = styled.button`
  padding: 0;
  -webkit-appearance: none;
  transition: box-shadow 0.2s;
  border: none;
  cursor: pointer;
  position: relative;
  border-radius: 8px;

  &:focus {
    outline: none;
  }

  &:focus > div[role="tooltip"],
  &:hover > div[role="tooltip"] {
    display: block;
  }

  @media (min-width: 768px) {
    &:focus > div[role="tooltip"],
    &:hover > div[role="tooltip"] {
      display: none;
    }
  }
`;
//endregion
